import { mdiTooltipTextOutline } from '@mdi/js'
import Icon from '@mdi/react'
import { dealerMonitorStates } from '@racepointenergy/swc/scs/dealer-monitor-states'
import { dealerMonitorSystemUsage } from '@racepointenergy/swc/scs/dealer-monitor-system-usage'
import {
  initialState,
  selectGlobalStatus,
} from '@racepointenergy/swc/scs/features/host-states'
import { StatusLight, SystemUsage } from '@racepointenergy/swc/scs/types/host'
import { useIntl } from 'react-intl'

import { ProgressCircle } from 'components/ProgressCircle'
import Tooltip from 'components/Tooltip'
import { statusColors } from 'features/home/consts'
import { ReactComponent as Uptime } from 'images/Uptime.svg'

import HomeLightStatuses from '../HomeAccess/LightStatuses'
import HomeConfigurableCard from '../HomeAccess/OverallStatus'
import { mainInfoCardsMessages } from '../messages'
import { percentageInversion } from '../../utils'
import { OverallSystemStatuses } from '../../consts'

interface HomeMetricsProps {
  homeId: string
}

const uptimeKeysFromMostImportant: (keyof SystemUsage['Uptime'])[] = [
  'Days',
  'Hours',
  'Minutes',
  'Seconds',
]

function uptimeCalculation(uptime?: SystemUsage['Uptime']): [number, string] {
  if (!uptime) return [0, '']

  for (let key of uptimeKeysFromMostImportant) {
    if (uptime[key]) {
      return [uptime[key], key.slice(0, 1).toLowerCase()]
    }
  }
  return [0, 's']
}

const HomeMetrics = ({ homeId }: HomeMetricsProps) => {
  const { formatMessage } = useIntl()

  const dealerMonitorSystem = dealerMonitorSystemUsage.useQuery({
    id: homeId,
  })

  const { data: states = initialState } = dealerMonitorStates.useQuery({
    id: homeId,
  })

  const { entities } = selectGlobalStatus(states)
  const {
    light: overallSystemStatusLight = StatusLight.Grey,
    status: overallSystemStatusText,
  } = entities['global.SystemStatus'] ?? {}
  const { labelText, labelColor, cardColor } =
    OverallSystemStatuses[overallSystemStatusLight]

  const [uptime, uptimeLabel] = uptimeCalculation(
    dealerMonitorSystem.data?.Uptime
  )

  const isDataExists = Boolean(dealerMonitorSystem.data)
  const isLoadingCardData = dealerMonitorSystem.isFetching

  const formattedLoadCPU = dealerMonitorSystem.data?.CPULoad.replace('%', '')
  const freeDiskSpacePercentage =
    dealerMonitorSystem.data?.FreeDiskSpacePercentage

  return (
    <>
      <HomeConfigurableCard
        hideOnDesktop
        title={formatMessage(mainInfoCardsMessages.overallStatusCardTitle)}
        label={
          <>
            {labelText}
            {overallSystemStatusText && (
              <Tooltip text={overallSystemStatusText} showOn="hover">
                <Icon
                  path={mdiTooltipTextOutline}
                  size={0.75}
                  color={statusColors[overallSystemStatusLight]}
                  style={{ marginLeft: '0.5rem' }}
                />
              </Tooltip>
            )}
          </>
        }
        cardColor={cardColor}
        labelColor={labelColor}
      />

      <HomeLightStatuses entities={entities} />

      <HomeConfigurableCard
        title={formatMessage(mainInfoCardsMessages.averageLoadCardTitle)}
        label={formattedLoadCPU || '-'}
        isLoading={isLoadingCardData}
        labelSymbol={isDataExists ? '%' : ''}
        iconPath={
          formattedLoadCPU && (
            <ProgressCircle percent={Number(formattedLoadCPU)} />
          )
        }
        width="32%"
      />

      <HomeConfigurableCard
        title={formatMessage(mainInfoCardsMessages.freeSpaceCardTitle)}
        label={freeDiskSpacePercentage || '-'}
        isLoading={isLoadingCardData}
        labelSymbol={isDataExists ? '%' : ''}
        iconPath={
          freeDiskSpacePercentage && (
            <ProgressCircle
              percent={Number(percentageInversion(freeDiskSpacePercentage))}
            />
          )
        }
        width="32%"
      />

      <HomeConfigurableCard
        title={formatMessage(mainInfoCardsMessages.uptimeCardTitle)}
        label={`${uptime || '-'}`}
        isLoading={isLoadingCardData}
        labelSymbol={uptimeLabel}
        iconPath={uptime && <Uptime />}
        width="32%"
      />
    </>
  )
}

export default HomeMetrics
